import { Dispatch, SetStateAction } from 'react';
import { useTheme } from 'styled-components';
import { Checkbox } from 'src/components/atoms';
import { DataDeletionType, useGetMessagesByReportQuery } from 'src/graphql';
import { FormState, Message } from 'src/types';
import { isBefore } from 'src/utils/time';
import { MessageDeletionList } from '../../../organisms/ReportMessages/MessageDeletionList';
import { InformationBanner } from '../../InformationBanner';
import { StepperModalHeader } from '../RemovePIIDataModal.styles';
import {
  ImageContainer,
  MessagesContainer,
  MessagesListContainer,
  MessagesStepContainer,
} from './MessagesStep.styles';

interface MessagesStepProps {
  warningMessage: {
    title?: string;
    description: string;
  };
  reportId: string;
  payload: DataDeletionType;
  setPayload: Dispatch<SetStateAction<DataDeletionType>>;
  messagesList?: Message[];
}

export const MessagesStep = ({
  warningMessage,
  reportId,
  payload,
  setPayload,
  messagesList,
}: MessagesStepProps & { messagesList?: Message[] }) => {
  const theme = useTheme();
  const emptyStateSrc = theme.isVaultVariant
    ? '/assets/empty-state.svg'
    : '/assets/empty-state-atlas.svg';
  const { data: messages } = useGetMessagesByReportQuery({
    report: reportId,
  });

  const handleMessageSelect = (id: string) => {
    const newSelections = payload.messages.includes(id)
      ? payload.messages.filter((alreadySelectedId) => alreadySelectedId !== id)
      : [...payload.messages, id];

    setPayload({ ...payload, messages: newSelections });
  };

  const rawMessages = messagesList ?? messages?.getMessagesByReport ?? [];

  const messagesWithType =
    rawMessages
      .map((message) => ({
        ...message,
        type: 'message' as const,
      }))
      .sort((a, b) => {
        if (isBefore(a.createdAt, b.createdAt)) {
          return -1;
        }

        return 1;
      }) ?? [];
  const notAlreadyDeletedMessages = messagesWithType
    .filter((message) => message.deletedAt === null)
    .map((message) => message.id);

  const handleMessageSelectAll = () => {
    if (payload.messages.length === notAlreadyDeletedMessages.length) {
      setPayload({ ...payload, messages: [] });
      return;
    }
    setPayload({ ...payload, messages: notAlreadyDeletedMessages });
  };

  return (
    <MessagesStepContainer>
      <StepperModalHeader data-testid="remove-pii-data-modal-header">
        {messagesWithType.length > 0
          ? 'Select messages to permanently remove'
          : 'No report messages available for deletion'}
      </StepperModalHeader>
      <InformationBanner
        state={FormState.WARNING}
        title={warningMessage.title ?? ''}
        description={warningMessage.description ?? ''}
        $centerIconVertically={!warningMessage.title}
      />
      <MessagesContainer>
        {messagesWithType.length === 0 ? (
          <ImageContainer>
            <img src={emptyStateSrc} alt="Empty state" width="100%" />
          </ImageContainer>
        ) : (
          <>
            <Checkbox
              checked={
                payload.messages.length === notAlreadyDeletedMessages.length
              }
              onChange={handleMessageSelectAll}
              disabled={notAlreadyDeletedMessages.length === 0}
              data-testid={`select-all-messages-checkbox`}
              label="Select all"
              labelPosition="right"
              labelSize="large"
              $round
            />
            <MessagesListContainer>
              <MessageDeletionList
                messagesList={messagesWithType}
                allowMessageSelect
                selectedMessages={payload.messages}
                onMessageSelect={handleMessageSelect}
              />
            </MessagesListContainer>
          </>
        )}
      </MessagesContainer>
    </MessagesStepContainer>
  );
};
